import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

/**
 * "Send" — email + WhatsApp, with a real PDF attached — for a Quotation,
 * Proforma, or the real generated Tax Invoice. Explicit and manual: nothing
 * sends automatically on Save & Issue.
 */

export interface SendChannelResult {
  /** The client has this contact method on file at all. */
  applicable: boolean;
  /** That channel (SMTP / WhatsApp Business API) is configured. */
  configured: boolean;
  sent: boolean;
  error: string | null;
}

export interface SendResult {
  email: SendChannelResult;
  whatsapp: SendChannelResult;
}

export interface SendDocumentResponse {
  ok: boolean;
  status?: string;
  result: SendResult;
}

function errorText(err: unknown, fallback: string) {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

export function useSendSalesDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<SendDocumentResponse>(`/api/documents/sales-doc/${id}/send`);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sales-doc'] });
      qc.invalidateQueries({ queryKey: ['quotations'] });
      qc.invalidateQueries({ queryKey: ['proforma'] });
    },
  });
}

export function useSendTaxInvoice() {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { data } = await api.post<SendDocumentResponse>(`/api/documents/invoice/${invoiceId}/send`);
      return data;
    },
  });
}

/** One human-readable line per channel that was actually applicable — a
 * channel the client has no contact info for is left out entirely rather
 * than reported as a failure. */
export function describeSendResult(result: SendResult): string {
  const parts: string[] = [];
  if (result.email.applicable) {
    parts.push(
      result.email.sent ? 'Emailed'
        : !result.email.configured ? 'Email not configured'
        : `Email failed (${result.email.error || 'unknown error'})`,
    );
  }
  if (result.whatsapp.applicable) {
    parts.push(
      result.whatsapp.sent ? 'sent via WhatsApp'
        : !result.whatsapp.configured ? 'WhatsApp not configured'
        : `WhatsApp failed (${result.whatsapp.error || 'unknown error'})`,
    );
  }
  return parts.length ? parts.join(', ') : 'Nothing to send — no channel available.';
}

export { errorText as sendErrorText };
